import { useEffect, useMemo, useState } from "react";

import { useRows, useSchema } from "../lib/api";
import type { SchemaColumn, SchemaTab } from "../lib/api";
import { todayIso } from "../lib/format";
import { Card } from "../components/Card";
import { ClaySelect } from "../components/ClaySelect";
import { DataTable } from "../components/DataTable";
import { DateRangePicker } from "../components/DateRangePicker";
import type { DateRange } from "../components/DateRangePicker";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { LoadingBlock } from "../components/Spinner";

const ROW_LIMIT = 1000;

export interface TabTablePageProps {
  /** Keep only these tab names (order preserved). Ignored when `exclude` is set. */
  only?: string[];
  /** Drop these tab names; used by the "Data Tim" page to catch the rest. */
  exclude?: string[];
  emptyTitle: string;
  emptyDescription: string;
}

/**
 * Read-only table over one or more sheet tabs: tab selector (only when more
 * than one tab matches), date range filter, CSV export. Shared by the Master
 * Data, Harian, Mingguan, and Data Tim pages.
 */
export function TabTablePage({ only, exclude, emptyTitle, emptyDescription }: TabTablePageProps) {
  const schema = useSchema();

  const tabs = useMemo<SchemaTab[]>(() => {
    const all = schema.data?.tabs ?? [];
    if (only) {
      return all.filter((tab) => only.includes(tab.name));
    }
    if (exclude) {
      return all.filter((tab) => !exclude.includes(tab.name));
    }
    return all;
    // Prop array literals change identity every render; the stringified form
    // is the real dependency.
  }, [schema.data, only?.join("|"), exclude?.join("|")]);

  const [tabName, setTabName] = useState("");
  const [range, setRange] = useState<DateRange>({ from: "", to: "" });

  useEffect(() => {
    if (tabs.length === 0) {
      setTabName("");
      return;
    }
    setTabName((current) =>
      tabs.some((tab) => tab.name === current) ? current : tabs[0].name,
    );
  }, [tabs]);

  const activeTab = tabs.find((tab) => tab.name === tabName) ?? null;

  const rowsState = useRows(
    activeTab
      ? {
          tab: activeTab.name,
          limit: ROW_LIMIT,
          from: range.from || undefined,
          to: range.to || undefined,
        }
      : null,
  );

  const columns: SchemaColumn[] = rowsState.data?.columns ?? activeTab?.columns ?? [];
  const rows = rowsState.data?.rows ?? [];
  const fileName = activeTab ? `${activeTab.name}-${todayIso()}` : "data";

  if (schema.loading) {
    return (
      <Card>
        <LoadingBlock message="Memuat konfigurasi data…" />
      </Card>
    );
  }

  if (schema.error) {
    return (
      <Card>
        <ErrorState error={schema.error} onRetry={schema.reload} />
      </Card>
    );
  }

  if (tabs.length === 0) {
    return (
      <Card>
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-5">
        {tabs.length > 1 ? (
          <ClaySelect
            label="Tab data"
            value={tabName}
            onValueChange={setTabName}
            options={tabs.map((tab) => ({ value: tab.name, label: tab.label }))}
          />
        ) : null}
        <DateRangePicker value={range} onChange={setRange} />
        <p className="text-xs text-muted">
          Menampilkan maksimum {ROW_LIMIT.toLocaleString("id-ID")} baris terbaru. Gunakan filter
          tanggal atau pencarian tabel untuk mempersempit data.
        </p>
      </Card>

      {rowsState.loading ? (
        <Card>
          <LoadingBlock message="Memuat baris data…" />
        </Card>
      ) : rowsState.error ? (
        <Card>
          <ErrorState error={rowsState.error} onRetry={rowsState.reload} />
        </Card>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          fileName={fileName}
          emptyTitle="Belum ada data"
          emptyDescription={
            range.from || range.to
              ? "Tidak ada baris pada rentang tanggal ini. Coba ubah filter tanggal."
              : "Tab ini belum memiliki baris data."
          }
        />
      )}
    </div>
  );
}
